import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, Phone, MapPin, Send, FileText, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Comm42" },
      {
        name: "description",
        content:
          "Get in touch with Comm42 for strategy, security, logistics and legal protection. Offices in Mesocco, Switzerland.",
      },
      { property: "og:title", content: "Contact — Comm42" },
      {
        property: "og:description",
        content:
          "Talk to Comm42 about your next move — global strategy, cyber security and private logistics.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://comm42.ch/contact" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/contact" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "@id": "https://comm42.ch/#organization",
          name: "Comm42 SA",
          url: "https://comm42.ch/contact",
          telephone: "+41918311215",
          email: "sales@comm42.eu",
          priceRange: "$$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "a la brona de Darba 7",
            postalCode: "6563",
            addressLocality: "Mesocco",
            addressRegion: "Canton Grigioni",
            addressCountry: "CH",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 46.4001,
            longitude: 9.2453,
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
          areaServed: "Worldwide",
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      setError(t("contact.error") as string);
    } finally {
      setSending(false);
    }
  }

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-card pt-40 pb-24">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_30%,oklch(0.6_0.15_240/0.45),transparent_55%),radial-gradient(circle_at_80%_70%,oklch(0.85_0.12_85/0.18),transparent_55%)]" />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <p className="text-xs tracking-[0.4em] text-primary">{t("contact.eyebrow")}</p>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl">{t("contact.title")}</h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">{t("contact.subtitle")}</p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Info */}
          <Reveal>
            <p className="text-xs tracking-[0.35em] text-primary">{t("contact.infoEyebrow")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl">{t("contact.infoTitle")}</h2>
            <p className="mt-6 text-muted-foreground">{t("contact.infoDesc")}</p>

            <ul className="mt-10 space-y-8">
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm tracking-[0.25em] text-foreground">{t("contact.address")}</h3>
                  <p className="mt-2 whitespace-pre-line text-muted-foreground">{t("contact.addressLines")}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm tracking-[0.25em] text-foreground">{t("contact.mailUs")}</h3>
                  <p className="mt-2 text-muted-foreground">
                    sales@comm42.eu
                    <br />
                    administration@comm42.eu
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm tracking-[0.25em] text-foreground">{t("contact.callUs")}</h3>
                  <p className="mt-2 text-muted-foreground">
                    <a href="tel:+41918311215" className="link-magnetic hover:text-primary">
                      +41 0918311215
                    </a>
                    <br />
                    <a href="tel:+41765013559" className="link-magnetic hover:text-primary">
                      +41 76 5013559
                    </a>
                  </p>
                </div>
              </li>
            </ul>

            <a
              href="https://www.comm42.eu/Comm42_Security%20Services.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-2 border border-primary px-6 py-3 text-xs tracking-[0.3em] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <FileText className="h-4 w-4" /> {t("contact.ourServices")} <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <form
              onSubmit={handleSubmit}
              className="rounded-lg border border-border/60 bg-card p-8 shadow-[var(--shadow-elegant)] md:p-10"
            >
              <h3 className="font-serif text-3xl">{t("contact.formTitle")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("contact.formDesc")}</p>

              <div className="mt-8 grid gap-5">
                <div>
                  <label htmlFor="name" className="text-xs tracking-[0.25em] text-muted-foreground">
                    {t("contact.name")}
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    maxLength={100}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full border-b border-border bg-transparent py-2 text-foreground outline-none transition-colors focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="text-xs tracking-[0.25em] text-muted-foreground">
                    {t("contact.email")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    maxLength={255}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full border-b border-border bg-transparent py-2 text-foreground outline-none transition-colors focus:border-primary"
                  />
                </div>
                <div>
                  <label
                    htmlFor="company"
                    className="text-xs tracking-[0.25em] text-muted-foreground"
                  >
                    {t("contact.company")}
                  </label>
                  <input
                    id="company"
                    type="text"
                    maxLength={120}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="mt-2 w-full border-b border-border bg-transparent py-2 text-foreground outline-none transition-colors focus:border-primary"
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="text-xs tracking-[0.25em] text-muted-foreground"
                  >
                    {t("contact.message")}
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={5}
                    maxLength={1500}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full resize-none border-b border-border bg-transparent py-2 text-foreground outline-none transition-colors focus:border-primary"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="mt-8 inline-flex items-center gap-2 bg-primary px-7 py-3 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:shadow-[0_10px_30px_-10px_oklch(0.5_0.09_235/0.6)]"
              >
                {sending ? "..." : t("contact.send")} <Send className="h-4 w-4" />
              </button>

              {submitted && (
                <p className="mt-4 text-sm text-primary">{t("contact.submitted")}</p>
              )}
              {error && (
                <p className="mt-4 text-sm text-destructive">{error}</p>
              )}
            </form>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="border-t border-border/40 bg-card/40">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <Reveal>
            <p className="text-xs tracking-[0.35em] text-primary">{t("contact.findUs")}</p>
            <h2 className="mt-4 font-serif text-4xl">{t("contact.findTitle")}</h2>
          </Reveal>
          <Reveal delay={120} className="mt-8 overflow-hidden rounded-lg border border-border/60">
            <iframe
              title="Comm42 office location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=9.225%2C46.385%2C9.265%2C46.415&layer=mapnik&marker=46.4%2C9.245"
              loading="lazy"
              className="h-[420px] w-full"
            />
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}